// Standard form wrapper. Renders an ErrorBanner for any caught
// mutation error, the form body, and a footer of Submit + Cancel
// buttons. Caller passes the submit handler — typical pattern:
//
//   <FormShell
//     submitLabel="Create tenant"
//     onSubmit={(e) => { e.preventDefault(); mutation.mutate(values); }}
//     onCancel={() => closeModal()}
//     pending={mutation.isPending}
//     error={mutation.error}
//   >
//     <TextField ... />
//   </FormShell>

import type { FormEvent, ReactNode } from "react";

import { ErrorBanner } from "../ui/ErrorBanner";

interface FormShellProps {
  children: ReactNode;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  error?: unknown;
  footer?: ReactNode;
  errorTitle?: string;
  // Disable submit independently of `pending` (e.g. read-only users, invalid
  // form). Cancel stays governed by `pending` only, so it remains clickable.
  submitDisabled?: boolean;
}

export function FormShell({
  children,
  onSubmit,
  onCancel,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  pending,
  error,
  footer,
  errorTitle,
  submitDisabled,
}: FormShellProps) {
  return (
    <form onSubmit={onSubmit} noValidate style={{ display: "grid", gap: 14 }}>
      {error ? <ErrorBanner error={error} title={errorTitle} /> : null}
      <div style={{ display: "grid", gap: 14 }}>{children}</div>
      {footer}
      <footer
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 4,
        }}
      >
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            style={secondaryButton}
          >
            {cancelLabel}
          </button>
        )}
        <button
          type="submit"
          disabled={pending || submitDisabled}
          style={primaryButton}
        >
          {pending ? "…" : submitLabel}
        </button>
      </footer>
    </form>
  );
}

const primaryButton: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  background: "transparent",
  color: "var(--text-dim)",
  border: "1px solid var(--border)",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  cursor: "pointer",
};
